/**
 * "Export my data" and "Delete my account" — the two controls that told a
 * visitor a legally significant request had been filed when nothing had.
 *
 * WHAT THEY DID. Both are arm-then-confirm toasts in Blake's account view, and
 * neither makes a server call (his bundle contains no fetch at all):
 *
 *   [data-acctexport]  app.js:4768  tap → "Tap again to request your data
 *                                   export" → "Request submitted · we'll
 *                                   compile and email your data within 30 days"
 *   [data-acctdelete]  app.js:4772  tap → "This permanently deletes your
 *                                   account and data" → "Are you absolutely
 *                                   sure? This cannot be undone." →
 *                                   "Deletion request received — we'll confirm
 *                                   by email before anything is removed"
 *
 * These are worse than the write-guard's usual customers. A fabricated "offer
 * sent" is a marketplace bug; a fabricated "deletion request received" tells
 * someone their account is on its way out when it is untouched, and a
 * fabricated data-export confirmation is a GDPR Art. 15/20 request that was
 * never filed, with a 30-day clock the copy starts and nothing ever answers.
 *
 * ── THE TWO ARE TREATED DIFFERENTLY, AND THE ASYMMETRY IS THE POINT ──────────
 *
 * ✅ EXPORT IS WIRED IN PLACE. POST /api/users/me/request-data-export
 * (users.ts:395) does exactly what the button says: files the request and
 * emails. The visitor stays in the marketplace, and the toast becomes true.
 *
 * 🔴 DELETE IS NOT WIRED, DELIBERATELY, and wiring it here would be worse than
 * leaving it lying. Two independent reasons:
 *
 *   1. THE COPY CONTRADICTS THE ENDPOINT. There is no request-based deletion
 *      flow — `DELETE /api/users/me` (users.ts:447) removes the account
 *      IMMEDIATELY; its own comment notes "the user row is gone by the time we
 *      send" the confirmation email. Blake's third toast promises the opposite:
 *      "we'll confirm by email BEFORE anything is removed". Wiring the button
 *      under that sentence means three taps and an irreversible deletion by
 *      someone who was told nothing had happened yet.
 *   2. IT WOULD SHIP A WEAKER SAFEGUARD THAN THE ONE THAT EXISTS. The real
 *      implementation — profile-settings-tab.tsx, the only client of that
 *      endpoint anywhere in the repo, mobile included — gates it on the visitor
 *      TYPING THEIR OWN USERNAME (`if (deleteConfirm !== username) return`).
 *      Blake's markup has no text input, so an interceptor cannot reproduce
 *      that. Replacing a typed confirmation with a triple-tap on an
 *      irreversible action is a downgrade we would be choosing.
 *
 * So the delete control ROUTES THE INTENT to the surface that implements it
 * properly. This is the redirect rule applied honestly rather than
 * reflexively: leave the marketplace only when it has no working surface for
 * the intent. Deleting an account qualifies — the destination is the only
 * correct implementation that exists, and the intent ENDS there, so nothing is
 * lost by not coming back.
 *
 * 🪤 NO INVENTED COPY, either way. The export toast is the SERVER's own
 * `message` field, exactly as profile-settings-tab.tsx renders it, falling back
 * to that file's own literal. The delete control shows nothing at all — it
 * navigates, and the destination speaks for itself.
 */

/** His two controls (app.js:4768, :4772). */
export const EXPORT_SELECTOR = "[data-acctexport]";
export const DELETE_SELECTOR = "[data-acctdelete]";

/** Verbatim from profile-settings-tab.tsx's own fallbacks. */
export const EXPORT_FALLBACK_MESSAGE = "Data export request submitted";
export const EXPORT_FAILURE_MESSAGE = "Request failed — please try again";

/**
 * The real deletion flow: the settings tab on its own route (option 2 for
 * #888, Blake 2026-09-18). It used to be `/profile?tab=settings`; /profile now
 * redirects into the web app and never forwards the query, so that address
 * would have sent the visitor to a page with no Delete Account on it.
 * accountSettingsRoute.test.tsx fails if this path ever resolves to a redirect.
 */
export const DELETE_FLOW_URL = "/account/settings";

/**
 * Said BEFORE the jump, because an unexplained page change is the same defect
 * this lane keeps fixing — it is what "Check out" did when it bounced to the
 * home feed, and the owner reported that as a dead button.
 *
 * 🪤 THE ONE PIECE OF INVENTED COPY IN THIS MODULE, and only because nothing
 * shipped covers it. Every other string here is the server's or
 * profile-settings-tab.tsx's. Blake's own labels ("Delete Account" /
 * "Permanently remove your account and data") are accurate, and the
 * destination's warning is already correct and strong — so this fills the one
 * real gap, WHY the page moved, without restating either. House voice: plain,
 * short, no scare wording, and it does not pre-empt the confirmation the
 * destination is about to give properly. Owner-approved 2026-08-28; Blake
 * should still get a look at it.
 */
export const DELETE_HANDOFF_MESSAGE =
  "Account deletion needs one more step. Opening your settings.";

interface AccountWindow extends Window {
  toast?: (message: string) => void;
}

export function installAccountActions(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  /** Seams for tests. `navigate` is a hard navigation: it leaves the bundle. */
  {
    notify = (message: string) => {
      if (!win.document.getElementById("toast")) return;
      (win as AccountWindow).toast?.(message);
    },
    navigate = (url: string) => win.location.assign(url),
  }: { notify?: (m: string) => void; navigate?: (url: string) => void } = {},
): () => void {
  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    if (!target?.closest) return;

    const del = target.closest(DELETE_SELECTOR) as HTMLElement | null;
    if (del) {
      /* Intercepted on the FIRST tap, not the third. His arm-then-confirm
         exists to guard a deletion that never happened; the real flow has its
         own, better confirmation, so making someone tap three times to be sent
         there is ceremony with nothing behind it. */
      event.stopPropagation();
      event.preventDefault();
      // Say it, THEN move. A silent jump is the defect, not the destination.
      notify(DELETE_HANDOFF_MESSAGE);
      navigate(DELETE_FLOW_URL);
      return;
    }

    const exp = target.closest(EXPORT_SELECTOR) as HTMLElement | null;
    if (!exp) return;

    /* 🪤 ONLY THE CONFIRM TAP. `armed` is set by HIS handler on the first
       click, so letting that one through is what preserves the arm step and
       its "Tap again to request your data export" prompt. Read before his
       listener runs — we are in the capture phase, and he binds on the element
       in the target phase. */
    if (!exp.dataset.armed) return;

    event.stopPropagation();
    event.preventDefault();
    delete exp.dataset.armed;

    void authFetch("/api/users/me/request-data-export", { method: "POST" })
      .then(async (res) => {
        if (!res.ok) {
          notify(EXPORT_FAILURE_MESSAGE);
          return;
        }
        // The server's own words, the way the real client renders them.
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        notify(body.message ?? EXPORT_FALLBACK_MESSAGE);
      })
      .catch(() => notify(EXPORT_FAILURE_MESSAGE));
  };

  win.document.addEventListener("click", onClick, true);
  return () => win.document.removeEventListener("click", onClick, true);
}
